/**
 * Shared chrome for the Role Approvals page.
 *
 * Kept beside the view rather than reached for from the design system where the design system has
 * no equivalent — the approval pills are a three-state signal (approved / declined / waiting) and
 * `Badge`'s variants are all soft tints with no "not yet" in them. The same documented deviation,
 * for the same reason, as `RatingBadge` in Role Prioritisation.
 */
import type { ApprovalDecision, ProposalStatus } from '../../types/roleApprovals';

/**
 * One radius for every box on this page. Imported rather than re-declared so it cannot drift from
 * the Org Planning Overview this page sits beside and links to, and re-exported so the approval
 * components take it from here with the rest of their chrome.
 *
 * ⚠️ Imported AND re-exported, not `export { RADIUS } from …`: that form does not bind the name in
 * this module's own scope, and the components below use it themselves. Same trap CLAUDE.md records
 * for `export *` in `rolePrioritisation.ts`.
 */
import { RADIUS } from '../orgPlanningChrome';

export { RADIUS };

const STATUS_STYLES: Record<ProposalStatus, string> = {
  Proposed: 'border-amber-200 bg-amber-50 text-amber-800',
  Approved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  Declined: 'border-red-200 bg-red-50 text-red-700',
  Cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-500',
};

const STATUS_LABELS: Record<ProposalStatus, string> = {
  Proposed: 'Awaiting approval',
  Approved: 'Approved',
  Declined: 'Declined',
  Cancelled: 'Cancelled',
};

export function StatusPill({ status }: { status: ProposalStatus | null }) {
  const key: ProposalStatus = status ?? 'Proposed';
  return (
    <span
      className={`inline-flex items-center ${RADIUS} border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[key]}`}
    >
      {STATUS_LABELS[key]}
    </span>
  );
}

/**
 * One approver's verdict.
 *
 * ⚠️ "Waiting" and "Declined" are deliberately different shapes, not two shades of the same one.
 * An undecided approver is the normal state of a fresh proposal; reading it as a soft no is how a
 * proposer concludes their role was rejected when nobody has looked at it yet.
 */
export function DecisionPill({ decision }: { decision: ApprovalDecision }) {
  if (decision === 'Approved') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
        <span aria-hidden>✓</span> Approved
      </span>
    );
  }
  if (decision === 'Declined') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700">
        <span aria-hidden>✕</span> Declined
      </span>
    );
  }
  // `zinc-500`, not the `zinc-400` used for this page's static footnotes: this is a status word
  // someone reads to decide whether to chase an approver, and 400 on white is under 3:1.
  return <span className="text-xs text-zinc-500">Waiting</span>;
}

/** A labelled fact on a proposal card. Renders nothing at all when the value is missing — an
 * empty "Unit: —" row costs a line and tells nobody anything. */
export function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex gap-1.5 text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className="text-zinc-700">{value}</span>
    </div>
  );
}

/** `2026-09-22T14:03:00.000Z` → `22 Sep 2026`. Dates only: the hour an approval landed is in the
 * audit log, and on a card it is noise. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
